import { useGetOnboardingStatusQuery } from 'store/api/customer.apislice';
import { useGetCurrentSubscriptionQuery } from 'store/api/subscription.apislice';
import { getInvestmentEligibility } from 'utils/investmentEligibility.utils';

export const usePlanGate = () => {
  const { data: onboardingData } = useGetOnboardingStatusQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const { data: currentSubscription, isLoading: isSubscriptionLoading } =
    useGetCurrentSubscriptionQuery();

  const isPaidSubscription =
    currentSubscription?.result?.status === 'ACTIVE' ||
    currentSubscription?.result?.current?.status === 'ACTIVE';

  const eligibility = getInvestmentEligibility(onboardingData, {
    isSubscriptionActive: isPaidSubscription,
  });

  const {
    customerType,
    isFreeCustomer,
    isDirect,
    shouldSkipPlanSelection,
  } = eligibility;

  const isSubscriptionActive = isPaidSubscription || isFreeCustomer || customerType === 'REGULAR';

  const isPlanDisabled =
    !isFreeCustomer &&
    (!customerType || (isDirect && !isSubscriptionActive));

  /**
   * Blocks a non-invest feature until the customer has picked a plan and, for
   * paid Direct, has paid, signed and completed risk profiling. Onboarding is
   * NOT enforced here — that belongs to useInvestGate, which raises it only
   * when the customer actually tries to transact.
   *
   * Returns true when the caller must stop because a step was raised.
   */
  const requirePlanSelection = () => {
    if (shouldSkipPlanSelection) return false;

    if (!customerType) {
      window.dispatchEvent(new Event('open-plan-selection-modal'));
      return true;
    }

    if (isDirect && !isFreeCustomer) {
      const { nextAction } = eligibility;

      if (nextAction === 'payment') {
        // Same race as useInvestGate: until the subscription has resolved, an
        // undefined response reads as "unpaid". Hold the action rather than
        // raise checkout against a status we do not know yet.
        if (isSubscriptionLoading || currentSubscription === undefined) {
          return true;
        }
        window.dispatchEvent(new Event('open-plan-selection-modal'));
        return true;
      }

      if (nextAction === 'agreement') {
        window.dispatchEvent(new Event('open-agreement-modal'));
        return true;
      }

      if (nextAction === 'risk') {
        window.dispatchEvent(new Event('open-risk-assessment-modal'));
        return true;
      }
    }

    return false;
  };

  return { isPlanDisabled, requirePlanSelection };
};
